/**
 * HiPro Custom Pack Builder -- client-side state + add-to-cart.
 *
 * Responsibilities:
 *  - Track packSize (2/5/10/20) and per-flavour quantities.
 *  - Keep the sticky sidebar (progress bar, summary, total, CTA) in sync.
 *  - Carry the delivery slot to checkout if one was chosen (see below).
 *  - On CTA click when pack is full: POST <cart-clear-url>, then POST
 *    <cart-add-url> with the chosen pack variant + flavour distribution
 *    as line-item properties, then POST <cart-update-url> with the
 *    Delivery Slot attribute, then redirect to <checkout-url>. On error,
 *    surface a visible inline message and restore the CTA via update().
 *
 * DELIVERY SLOT (added 27 Aug 2026 pm). This page goes straight to
 * checkout, so the cart-page / drawer picker was never reachable from
 * here and most orders arrived with no `Delivery Slot` -- which holds
 * the kitchen message in hipro-web-order-to-unleashed. The picker now
 * renders in the sidebar (snippets/hipro-delivery-slot.liquid, ctx
 * "builder") and this file:
 *   1. (until 9 Sep 2026) refused to submit until a slot was chosen - REMOVED.
 *      Weng, 9 Sep 2026: never stand between a buyer and payment. An order
 *      with no slot is held by the pipeline for a human to confirm the time.
 *   2. re-posts the attribute AFTER cart/add.js succeeds (an empty value
 *      clears a stale one).
 * Step 2 is not optional. A stale slot from an earlier visit would
 * otherwise ride along untouched on the cart attributes, which this
 * file no longer clears at all (see the submit handler: the cart/clear
 * call was removed 28 Aug 2026 so packs can be bought together).
 * Writing it explicitly at submit is the only thing that
 * guarantees the attribute matches what this customer just picked,
 * without trusting the widget's own change-time save. The redirect
 * only happens once that update returns ok -- a silent failure here
 * would put an order through with the wrong slot, or none.
 *
 * Locale-safe routing: cart-add, cart-clear, cart-update, and checkout
 * URLs come from data attributes on the section root (server-rendered
 * from Shopify's routes object). Hard-coded "/cart/add.js" etc. would
 * break on locale-prefixed stores like /en/cart/add.js.
 *
 * XSS hygiene: no innerHTML for any dynamic content. Use textContent +
 * createElement + appendChild. Merchant-controlled inputs are still
 * escaped by default as a discipline.
 *
 * Source-encoding note: this file is pure ASCII. Display glyphs are
 * declared as \u escapes so the file ships safely regardless of how a
 * server / CDN tags its charset.
 */
(function () {
  'use strict';

  // Display glyphs assembled via fromCharCode -- keeps the source pure
  // ASCII so the file ships safely regardless of how a server / CDN tags
  // its charset. Mapping:
  //   0x2192 RIGHT ARROW (matches Liquid entity &rarr;)
  //   0x2212 MINUS SIGN  (matches &minus;)
  //   0x00D7 MULTIPLICATION SIGN (matches &times;)
  //   0x2026 HORIZONTAL ELLIPSIS (matches &hellip;)
  //   0x2014 EM DASH (matches &mdash;)
  var ARROW_RIGHT = String.fromCharCode(0x2192);
  var MINUS = String.fromCharCode(0x2212);
  var TIMES = String.fromCharCode(0x00D7);
  var ELLIPSIS = String.fromCharCode(0x2026);
  var EM_DASH = String.fromCharCode(0x2014);

  var SLOT_PROMPT = 'Pick a delivery time ' + EM_DASH + ' morning or afternoon.';

  var root = document.querySelector('[data-pack-builder]');
  if (!root) return;

  // ---- Config from data attributes -----------------------------------------

  // NOTE: read these via getAttribute, NOT root.dataset.* -- the HTML dataset
  // API does NOT camelCase a hyphen that precedes a digit, so `data-pack-5-id`
  // becomes dataset['pack-5Id'] (not dataset.pack5Id). Using dataset.pack5Id
  // silently returned undefined, which broke pack selection, checkout, and the
  // discount line. getAttribute with the literal attribute name is spelling-proof.
  var variantIds = {
    2: root.getAttribute('data-pack-2-id'),
    5: root.getAttribute('data-pack-5-id'),
    10: root.getAttribute('data-pack-10-id'),
    20: root.getAttribute('data-pack-20-id'),
  };

  var discounts = {
    2: parseFloat(root.getAttribute('data-discount-2')) || 0,
    5: parseFloat(root.getAttribute('data-discount-5')) || 0,
    10: parseFloat(root.getAttribute('data-discount-10')) || 0,
    20: parseFloat(root.getAttribute('data-discount-20')) || 0,
  };

  var MEAL_PRICE = parseFloat(root.dataset.mealPrice) || 14.8;
  var CURRENCY_PREFIX = 'RM';

  // Routes from Liquid (locale-safe). All are required; if any is
  // missing, fall back to the canonical English paths so the builder
  // still works on a default-locale install.
  var cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
  // Retained for the data-attribute contract only. NOT USED: this builder no
  // longer clears the cart (28 Aug 2026). See the submit handler.
  var cartClearUrl = root.dataset.cartClearUrl || '/cart/clear.js';
  var cartUpdateUrl = root.dataset.cartUpdateUrl || '/cart/update.js';
  var checkoutUrl = root.dataset.checkoutUrl || '/checkout';

  // ---- State ---------------------------------------------------------------

  // 10 is only a last-resort fallback -- the real default is read from the
  // server-marked active tile just below, so JS state never disagrees with
  // the rendered markup (taster tile when configured).
  var packSize = 10;
  var quantities = Object.create(null); // { flavourName: qty }

  // ---- DOM references ------------------------------------------------------

  var sizeButtons = root.querySelectorAll('[data-pack-sizes] [data-size]');
  var defaultSizeBtn = root.querySelector('[data-pack-sizes] [aria-pressed="true"]');
  if (defaultSizeBtn) {
    packSize = parseInt(defaultSizeBtn.dataset.size, 10) || packSize;
  }
  var flavourRows = root.querySelectorAll('[data-flavour-row]');
  var targetEls = root.querySelectorAll('[data-pack-target]');
  // querySelectorAll, not querySelector: the count renders both in the
  // sidebar and in the mobile sticky bar.
  var nowEls = root.querySelectorAll('[data-pack-now]');
  var fillEl = root.querySelector('[data-pack-fill]');
  var hintEl = root.querySelector('[data-pack-hint]');
  var summaryEl = root.querySelector('[data-pack-summary]');
  var ctaEl = root.querySelector('[data-pack-cta]');
  var variantInput = root.querySelector('[data-pack-variant-id]');
  var formEl = root.querySelector('[data-pack-form]');
  var errorEl = root.querySelector('[data-pack-error]');
  var noJsNoteInput = root.querySelector('[data-no-js-note]');
  var barEl = root.querySelector('[data-pack-mobilebar]');
  var barCtaEl = root.querySelector('[data-pack-bar-cta]');
  var barTotalEl = root.querySelector('[data-pack-bar-total]');
  var barSlotEl = root.querySelector('[data-pack-bar-slot]');
  var sidebarEl = root.querySelector('[data-pack-sidebar]');

  // The delivery-slot widget rendered with ctx "builder". Its own inline
  // script runs at parse time (this file is deferred), so root.hiproSlot
  // is always hung on by the time we get here -- but read defensively
  // anyway so a future render order change degrades to the attribute.
  var slotEl = root.querySelector('[data-hipro-slot][data-ctx="builder"]');

  // Bail out gracefully if the flavours collection wasn't configured -- the
  // Liquid renders an inline error block in that case and skips the builder.
  if (!flavourRows.length || !ctaEl || !formEl) return;

  // JS is alive -- remove the no-JS kitchen note so it never reaches the
  // cart on a healthy submit. (Liquid renders it as a fallback so the
  // operator sees a flag if the form somehow POSTs without JS hydration.)
  if (noJsNoteInput && noJsNoteInput.parentNode) {
    noJsNoteInput.parentNode.removeChild(noJsNoteInput);
  }

  // ---- Delivery slot helpers ----------------------------------------------

  function slotValue() {
    if (!slotEl) return '';
    if (slotEl.hiproSlot && typeof slotEl.hiproSlot.getValue === 'function') {
      return slotEl.hiproSlot.getValue();
    }
    return slotEl.getAttribute('data-slot-value') || '';
  }

  // Pull the customer's eye to the picker. On mobile the sidebar sits far
  // above the sticky bar, so an error message alone can be entirely
  // off-screen -- scroll + highlight is what actually communicates.
  function slotComplain() {
    if (!slotEl) return;
    if (slotEl.hiproSlot) {
      if (typeof slotEl.hiproSlot.showError === 'function') {
        slotEl.hiproSlot.showError(SLOT_PROMPT);
      }
      if (typeof slotEl.hiproSlot.flash === 'function') {
        slotEl.hiproSlot.flash();
        return;
      }
    }
    try {
      slotEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      slotEl.scrollIntoView();
    }
  }

  function slotClearComplaint() {
    if (slotEl && slotEl.hiproSlot && typeof slotEl.hiproSlot.clearError === 'function') {
      slotEl.hiproSlot.clearError();
    }
  }

  // Mirror the choice into the mobile sticky bar. It is a LABEL, never a
  // second set of radios -- duplicating the widget would split one answer
  // across two controls and collide on ids.
  function renderBarSlot() {
    if (!barSlotEl) return;
    var value = slotValue();
    if (value) {
      barSlotEl.textContent = value.split(' (')[0];
      barSlotEl.classList.remove('hipro-pack-builder__mobilebar-slot--missing');
    } else {
      barSlotEl.textContent = 'Delivery time: optional';
      barSlotEl.classList.add('hipro-pack-builder__mobilebar-slot--missing');
    }
  }

  if (slotEl) {
    slotEl.addEventListener('hipro-slot:change', function () {
      renderBarSlot();
      clearError();
    });
  }

  // ---- Helpers -------------------------------------------------------------

  function totalQty() {
    var sum = 0;
    for (var name in quantities) {
      sum += quantities[name];
    }
    return sum;
  }

  function formatMoney(n) {
    return CURRENCY_PREFIX + n.toFixed(2);
  }

  // Effective per-meal price for the CURRENT pack size (base price minus the
  // pack discount). The Liquid renders the base price server-side; without
  // this, the flavour rows kept saying RM14.80 while the tiles said RM14.06 /
  // RM13.32 / RM12.50 -- two different prices at the decision moment.
  function effectivePerMeal() {
    var pct = discounts[packSize] || 0;
    return MEAL_PRICE * (1 - pct / 100);
  }

  function updateFlavourPrices() {
    var per = formatMoney(effectivePerMeal());
    flavourRows.forEach(function (row) {
      var el = row.querySelector(
        '[data-flavour-price], .hipro-pack-builder__flavour-price'
      );
      if (el) el.textContent = per + ' each';
    });
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  // ---- Pack-size selection -------------------------------------------------

  sizeButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var newSize = parseInt(btn.dataset.size, 10);
      if (!variantIds[newSize]) return;
      // Clicking the already-active tile would otherwise wipe a half-built
      // pack -- early-return preserves the customer's progress.
      if (newSize === packSize) return;

      sizeButtons.forEach(function (b) {
        b.classList.remove('hipro-pack-builder__size--active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('hipro-pack-builder__size--active');
      btn.setAttribute('aria-pressed', 'true');

      packSize = newSize;
      variantInput.value = variantIds[packSize];

      targetEls.forEach(function (el) {
        el.textContent = String(packSize);
      });

      // Reset all flavour quantities -- cleanest UX when switching sizes.
      Object.keys(quantities).forEach(function (k) {
        quantities[k] = 0;
      });
      flavourRows.forEach(function (row) {
        var val = row.querySelector('[data-qty-val]');
        if (val) {
          val.textContent = '0';
          val.classList.add('hipro-pack-builder__qty-val--zero');
        }
        row.classList.remove('hipro-pack-builder__flavour--has-qty');
      });

      clearError();
      update();
    });
  });

  // ---- Per-flavour quantity controls --------------------------------------

  flavourRows.forEach(function (row) {
    var name = row.dataset.flavourName;
    quantities[name] = 0;

    var valEl = row.querySelector('[data-qty-val]');
    var incBtn = row.querySelector('[data-qty-inc]');
    var decBtn = row.querySelector('[data-qty-dec]');

    function setQty(q) {
      quantities[name] = q;
      if (valEl) {
        valEl.textContent = String(q);
        if (q > 0) {
          valEl.classList.remove('hipro-pack-builder__qty-val--zero');
          row.classList.add('hipro-pack-builder__flavour--has-qty');
        } else {
          valEl.classList.add('hipro-pack-builder__qty-val--zero');
          row.classList.remove('hipro-pack-builder__flavour--has-qty');
        }
      }
    }

    if (incBtn) {
      incBtn.addEventListener('click', function () {
        if (totalQty() >= packSize) return; // hard cap at pack size
        setQty(quantities[name] + 1);
        clearError();
        update();
      });
    }

    if (decBtn) {
      decBtn.addEventListener('click', function () {
        if (quantities[name] <= 0) return;
        setQty(quantities[name] - 1);
        clearError();
        update();
      });
    }
  });

  // ---- Sync UI -------------------------------------------------------------

  function update() {
    var total = totalQty();
    var remaining = packSize - total;

    nowEls.forEach(function (el) {
      el.textContent = String(total);
    });

    if (fillEl) {
      var pct = Math.min(100, (total / packSize) * 100);
      fillEl.style.width = pct + '%';
    }

    // "Pick" wording everywhere -- matches the "Pick a pack size" header
    // and the CTA label so the customer sees one verb throughout.
    if (hintEl) {
      if (remaining > 0) {
        hintEl.textContent =
          'Pick ' + remaining + ' more to fill the pack.';
      } else {
        hintEl.textContent = 'Pack ready.';
      }
    }

    // Grey out the + buttons when at cap so customers see why they can't
    // add more.
    flavourRows.forEach(function (row) {
      var incBtn = row.querySelector('[data-qty-inc]');
      if (!incBtn) return;
      if (total >= packSize) {
        incBtn.setAttribute('disabled', '');
      } else {
        incBtn.removeAttribute('disabled');
      }
    });

    // CTA state -- mirrored onto the mobile bar CTA so both stay in step.
    //
    // NOTE the CTA is deliberately NOT disabled when only the delivery slot
    // is missing. A dead button explains nothing; letting the click through
    // to the submit handler is what surfaces the message and scrolls the
    // picker into view.
    var ctaLabel;
    if (remaining > 0) {
      ctaLabel = 'Pick ' + remaining + ' more ' + ARROW_RIGHT;
    } else {
      ctaLabel = 'Checkout ' + ARROW_RIGHT;
    }
    [ctaEl, barCtaEl].forEach(function (el) {
      if (!el) return;
      el.textContent = ctaLabel;
      if (remaining > 0) {
        el.setAttribute('disabled', '');
        el.classList.add('hipro-pack-builder__cta--disabled');
      } else {
        el.removeAttribute('disabled');
        el.classList.remove('hipro-pack-builder__cta--disabled');
      }
    });

    updateFlavourPrices();
    renderSummary();
    renderBarSlot();
  }

  function renderSummary() {
    if (!summaryEl) return;

    // Clear without using innerHTML.
    while (summaryEl.firstChild) {
      summaryEl.removeChild(summaryEl.firstChild);
    }

    var total = totalQty();
    var subtotal = 0;

    Object.keys(quantities).forEach(function (name) {
      var qty = quantities[name];
      if (qty <= 0) return;

      var lineTotal = MEAL_PRICE * qty;
      subtotal += lineTotal;

      var row = document.createElement('div');
      row.className = 'hipro-pack-builder__summary-row';

      var label = document.createElement('span');
      label.className = 'hipro-pack-builder__summary-label';
      label.textContent = name + ' ' + TIMES + ' ' + qty;

      var amount = document.createElement('span');
      amount.className = 'hipro-pack-builder__summary-amount';
      amount.textContent = formatMoney(lineTotal);

      row.appendChild(label);
      row.appendChild(amount);
      summaryEl.appendChild(row);
    });

    // Discount line (if applicable for this pack size and any meals picked).
    var discountPct = discounts[packSize] || 0;
    if (subtotal > 0 && discountPct > 0) {
      var discountAmount = subtotal * (discountPct / 100);

      var dRow = document.createElement('div');
      dRow.className =
        'hipro-pack-builder__summary-row hipro-pack-builder__summary-row--discount';

      var dLabel = document.createElement('span');
      dLabel.className = 'hipro-pack-builder__summary-label';
      // The taster's "discount" is a derived number that backs into the fixed
      // RM price -- showing a long decimal % would look broken. Friendly label.
      // Whole percent only, and below 5% the number sells nothing - say
      // "Pack price" instead of "Pack discount (1.33%)". (9 Sep 2026)
      var roundedPct = Math.round(discountPct);
      dLabel.textContent =
        packSize === 2
          ? 'Taster deal'
          : roundedPct >= 5
            ? 'Pack discount (' + roundedPct + '%)'
            : 'Pack price';

      var dAmount = document.createElement('span');
      dAmount.className = 'hipro-pack-builder__summary-amount';
      dAmount.textContent = MINUS + ' ' + formatMoney(discountAmount);

      dRow.appendChild(dLabel);
      dRow.appendChild(dAmount);
      summaryEl.appendChild(dRow);

      subtotal = subtotal - discountAmount;
    }

    // Total -- only show once the pack has at least one meal.
    if (total > 0) {
      var tRow = document.createElement('div');
      tRow.className =
        'hipro-pack-builder__summary-row hipro-pack-builder__summary-row--total';

      var tLabel = document.createElement('span');
      tLabel.className = 'hipro-pack-builder__summary-label';
      tLabel.textContent = 'Total';

      var tAmount = document.createElement('span');
      tAmount.className = 'hipro-pack-builder__summary-amount';
      tAmount.textContent = formatMoney(subtotal);

      tRow.appendChild(tLabel);
      tRow.appendChild(tAmount);
      summaryEl.appendChild(tRow);
    }

    // Mirror the running total onto the mobile sticky bar.
    if (barTotalEl) {
      barTotalEl.textContent = formatMoney(subtotal);
    }
  }

  // ---- Form submit -- intercept and POST via fetch -----------------------

  formEl.addEventListener('submit', function (e) {
    e.preventDefault();

    if (totalQty() < packSize) {
      // Belt-and-braces: the CTA is disabled in this state, but if a screen
      // reader or keyboard user reaches the form some other way, still block.
      showError('Pick ' + (packSize - totalQty()) + ' more meals first.');
      return;
    }

    // Delivery slot is OPTIONAL since 9 Sep 2026 (was a hard gate). An
    // empty value is still written below so a stale choice never rides
    // along; the pipeline holds slot-less orders for a human.
    var slot = slotValue();

    var variantId = variantIds[packSize];
    if (!variantId) {
      showError('Pack variant not configured. Please contact us so we can fix this.');
      return;
    }

    // Build the properties object -- only include flavours with qty > 0.
    var properties = {};
    Object.keys(quantities).forEach(function (name) {
      var qty = quantities[name];
      if (qty > 0) {
        properties[name] = qty;
      }
    });

    ctaEl.setAttribute('disabled', '');
    ctaEl.textContent = 'Adding' + ELLIPSIS;
    if (barCtaEl) {
      barCtaEl.setAttribute('disabled', '');
      barCtaEl.textContent = 'Adding' + ELLIPSIS;
    }
    clearError();
    slotClearComplaint();

    // ADD ONLY -- the cart is NOT cleared first. Changed 28 Aug 2026 on Weng's
    // instruction. The old flow called cart/clear.js before every add, which
    // wiped EVERY other line: building a 10-pack silently deleted a 5-pack the
    // customer had already added, so no order could ever contain more than one
    // pack. The Taster CTA never cleared, which is why only it survived.
    // Weng chose "never remove anything" knowing the trade-off: rebuilding the
    // same size now ADDS a second line instead of replacing it. That is
    // visible -- this flow lands on checkout, whose summary lists every line.
    // Do not re-introduce cart/clear.js here without asking him again.
    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: properties,
          },
        ],
      }),
    })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (body) {
            throw new Error('Add to cart failed: ' + res.status + ' ' + body);
          });
        }
        // Slot attribute goes on LAST, after the add: this is the
        // authoritative write of what the customer picked on THIS visit,
        // overwriting anything cart/clear.js left standing. Do not move it
        // earlier and do not rely on the picker's own change-time save.
        return fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          // 'Delivery Day' cleared as well - a stale one from an old visit
          // survives /cart/clear.js (seen live 9 Sep 2026).
          body: JSON.stringify({ attributes: { 'Delivery Slot': slot, 'Delivery Day': '' } }),
        });
      })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (body) {
            throw new Error('Delivery slot save failed: ' + res.status + ' ' + body);
          });
        }
        // Only now is the order safe to hand to checkout.
        window.location.href = checkoutUrl;
      })
      .catch(function (err) {
        // Log for ops, but show a customer-safe message -- never the raw error.
        console.error('[hipro-pack-builder]', err);
        showError(
          'We could not add your pack to the cart. Please refresh and try again.'
        );
        // Hand the CTA state back to update() -- single source of truth for
        // the label and disabled flag, so we never drift from the rest of
        // the UI on error recovery.
        update();
      });
  });

  // ---- Mobile sticky bar ---------------------------------------------------

  if (barEl) {
    // JS is alive, so the bar is functional -- reveal it. CSS keeps it
    // display:none on desktop regardless.
    barEl.hidden = false;

    if (barCtaEl && ctaEl) {
      barCtaEl.addEventListener('click', function () {
        // Delegate to the real submit button inside the form so the
        // existing submit handler (validation, cart calls, redirect) stays
        // the single source of truth. (The slot gate that used to live here
        // was removed 9 Sep 2026 - the slot is optional now.)
        ctaEl.click();
      });
    }

    // Slide the bar away while the real sidebar is on screen -- otherwise
    // the customer sees the same numbers twice at the bottom of the page.
    if ('IntersectionObserver' in window && sidebarEl) {
      var io = new IntersectionObserver(
        function (entries) {
          barEl.classList.toggle(
            'hipro-pack-builder__mobilebar--off',
            entries[0].isIntersecting
          );
        },
        { threshold: 0.15 }
      );
      io.observe(sidebarEl);
    }
  }

  // ---- Init ----------------------------------------------------------------

  // The markup marks the default tile active (taster when configured);
  // packSize was read back from it at the top of this file.
  variantInput.value = variantIds[packSize];
  update();

  // ---- Deep-link prefill ----------------------------------------------------
  // Ad / campaign links can preselect a pack size and flavour, e.g.
  //   /pages/build-your-pack?size=2&flavour=pedas
  // size:   activates the matching pack tile (reuses the click handler so all
  //         reset + state logic stays in one place).
  // flavour: case-insensitive substring match on the flavour name; fills the
  //         whole pack for the 2-meal taster (one tap from checkout), or seeds
  //         1 meal on bigger packs so the customer's flavour is already chosen.
  // A deep link never preselects a delivery slot -- that stays the customer's
  // own tap, exactly as on the cart page.
  // Any malformed input is ignored -- the page just behaves like a plain visit.
  try {
    var params = new URLSearchParams(window.location.search);

    var sizeParam = parseInt(params.get('size'), 10);
    if (sizeParam && sizeParam !== packSize && variantIds[sizeParam]) {
      sizeButtons.forEach(function (btn) {
        if (parseInt(btn.dataset.size, 10) === sizeParam) btn.click();
      });
    }

    var flavourParam = (params.get('flavour') || '').trim().toLowerCase();
    if (flavourParam) {
      var matched = false;
      flavourRows.forEach(function (row) {
        if (matched) return;
        var name = (row.dataset.flavourName || '').toLowerCase();
        if (name.indexOf(flavourParam) === -1) return;
        matched = true;
        var incBtn = row.querySelector('[data-qty-inc]');
        if (!incBtn) return;
        var prefill = packSize === 2 ? 2 : 1;
        for (var i = 0; i < prefill; i++) incBtn.click();
      });
    }
  } catch (err) {
    // URLSearchParams unsupported or bad input -- plain visit, no prefill.
  }
})();
